/* eslint-disable @geist/no-unknown-type -- RPC/JSON-RPC protocol types require unknown for envelope generics, method handlers, logger data, and error details at the wire boundary */
/**
 * @geist/rpc-core
 *
 * Core RPC types and utilities for GEIST communication.
 *
 * Features:
 * - Wire format (Envelope) for all RPC messages
 * - Connection state management
 * - Handler context types
 * - Error codes and utilities
 *
 * @example
 * ```typescript
 * import {
 *   Envelope,
 *   createCallEnvelope,
 *   createResponseEnvelope,
 *   isCallEnvelope,
 *   RPCErrorCode,
 * } from '@geist/rpc-core';
 *
 * // Create a call
 * const call = createCallEnvelope('memories.search', { query: 'test' });
 *
 * // Create a response
 * const response = createResponseEnvelope(call.id, { results: [] });
 * ```
 */

// ═══════════════════════════════════════════════════════════════
// ENVELOPE TYPES
// ═══════════════════════════════════════════════════════════════

/**
 * Message types for RPC communication.
 */
export type EnvelopeType = 'call' | 'response' | 'event' | 'error' | 'system';

/**
 * Wire format for all RPC messages.
 *
 * This is the canonical envelope format used throughout GEIST for
 * WebSocket-based RPC communication.
 */
export interface Envelope<T = unknown> {
  /** Message type: call (request), response, event, error, system */
  readonly type: EnvelopeType;

  /** Unique message ID for request/response correlation */
  readonly id: string;

  /**
   * The method or event name: "scope.method" or "scope.$event"
   *
   * Events are prefixed with $ (e.g., "memories.$created").
   * Methods use dot notation (e.g., "memories.search").
   */
  readonly method: string | null;

  /** The payload data */
  readonly data: T;

  /**
   * Error details (for type: 'error' or 'response' with error)
   */
  readonly error: EnvelopeError | null;

  /** Timestamp when the message was created (ms since epoch) */
  readonly ts: number;

  /**
   * Optional position for outbox/sync (monotonic sequence).
   * Used for resumable subscriptions and reconnection.
   */
  readonly pos: string | null;
}

/**
 * Error structure within an envelope.
 */
export interface EnvelopeError {
  readonly code: string;
  readonly message: string;
  readonly details: unknown | null;
}

// ═══════════════════════════════════════════════════════════════
// CONNECTION STATE
// ═══════════════════════════════════════════════════════════════

/**
 * Connection lifecycle states.
 */
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'syncing'
  | 'ready'
  | 'reconnecting'
  | 'closed';

/**
 * Worker-specific connection states (includes registration).
 */
export type WorkerConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'registering'
  | 'ready'
  | 'reconnecting';

// ═══════════════════════════════════════════════════════════════
// HANDLER CONTEXT
// ═══════════════════════════════════════════════════════════════

/**
 * User information from authentication.
 */
export interface ContextUser {
  readonly id: string;
  readonly username: string | null;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly projectId: string | null;
  readonly role: string | null;
  readonly scopes: readonly string[];
}

/**
 * Logger interface for handler context.
 */
export interface ContextLogger {
  debug: (msg: string, data: unknown | null) => void;
  info: (msg: string, data: unknown | null) => void;
  warn: (msg: string, data: unknown | null) => void;
  error: (msg: string, data: unknown | null) => void;
}

/**
 * Context passed to RPC handlers.
 */
export interface HandlerContext {
  /** Unique ID for this request (for correlation) */
  readonly messageId: string;

  /** User identity from authentication */
  readonly user: ContextUser | null;

  /** Emit an event to subscribers */
  emit: <T>(event: string, data: T) => void;

  /** Broadcast an event to all connections for this user */
  broadcast: <T>(event: string, data: T) => void;

  /** Logging helpers */
  log: ContextLogger;

  /** Send a binary audio frame to the requesting client. Null if transport does not support binary. */
  sendBinaryFrame: ((streamId: string, chunk: Buffer, isLast: boolean) => void) | null;
}

// ═══════════════════════════════════════════════════════════════
// BINARY FRAME UTILITIES
// ═══════════════════════════════════════════════════════════════

export const BINARY_STREAM_ID_BYTES = 36; // UUID padded to 36 ASCII bytes
export const BINARY_HEADER_SIZE = 37; // 36 + 1 isLast byte

/** Encode a binary audio frame: [36 bytes streamId][1 byte isLast][chunk] */
export function buildBinaryFrame(streamId: string, chunk: Buffer, isLast: boolean): Buffer {
  const idBuf = Buffer.alloc(36);
  idBuf.write(streamId.padEnd(36).slice(0, 36), 'ascii');
  const flagBuf = Buffer.alloc(1);
  flagBuf[0] = isLast ? 1 : 0;
  return Buffer.concat([idBuf, flagBuf, chunk]);
}

/** Decode a binary audio frame. Returns null if buffer is too short. */
export function parseBinaryFrame(
  buf: Buffer,
): { streamId: string; isLast: boolean; audio: Buffer } | null {
  if (buf.length < BINARY_HEADER_SIZE) return null;
  const streamId = buf.subarray(0, 36).toString('ascii').trimEnd();
  const isLast = buf.readUInt8(36) === 1;
  const audio = buf.subarray(37);
  return { streamId, isLast, audio };
}

/**
 * Handler function type for methods.
 */
export type MethodHandler<TInput = unknown, TOutput = unknown> = (
  input: TInput,
  ctx: HandlerContext,
) => Promise<TOutput>;

// ═══════════════════════════════════════════════════════════════
// ERROR CODES
// ═══════════════════════════════════════════════════════════════

/**
 * Standard RPC error codes.
 *
 * Uses negative numbers for protocol errors (similar to JSON-RPC).
 * Application errors use positive numbers or string codes.
 */
export enum RPCErrorCode {
  // Protocol errors (negative)
  ParseError = -32700,
  InvalidRequest = -32600,
  MethodNotFound = -32601,
  InvalidParams = -32602,
  InternalError = -32603,

  // Transport errors
  ConnectionClosed = -32000,
  ConnectionTimeout = -32001,
  RequestTimeout = -32002,

  // Application errors
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  RateLimited = 429,
}

/**
 * RPC error class with code and details.
 */
export class RPCError extends Error {
  readonly code: string | number;
  readonly details: unknown | null;

  constructor(code: string | number, message: string, details: unknown | null = null) {
    super(message);
    this.name = 'RPCError';
    this.code = code;
    this.details = details;
  }

  toEnvelopeError(): EnvelopeError {
    return {
      code: String(this.code),
      message: this.message,
      details: this.details,
    };
  }

  static fromEnvelopeError(error: EnvelopeError): RPCError {
    return new RPCError(error.code, error.message, error.details);
  }
}

// ═══════════════════════════════════════════════════════════════
// ENVELOPE FACTORIES
// ═══════════════════════════════════════════════════════════════

let idCounter = 0;

/**
 * Generate a unique message ID.
 */
export function generateId(): string {
  return `${Date.now()}-${++idCounter}`;
}

/**
 * Reset the ID counter (for testing).
 */
export function resetIdCounter(): void {
  idCounter = 0;
}

/**
 * Create a call (request) envelope.
 */
export function createCallEnvelope<T>(
  method: string,
  data: T,
  id: string | null = null,
): Envelope<T> {
  return {
    type: 'call',
    id: id ?? generateId(),
    method,
    data,
    error: null,
    ts: Date.now(),
    pos: null,
  };
}

/**
 * Create a response envelope.
 */
export function createResponseEnvelope<T>(requestId: string, data: T): Envelope<T> {
  return {
    type: 'response',
    id: requestId,
    method: null,
    data,
    error: null,
    ts: Date.now(),
    pos: null,
  };
}

/**
 * Create an error response envelope.
 */
export function createErrorEnvelope(requestId: string, error: EnvelopeError): Envelope<null> {
  return {
    type: 'error',
    id: requestId,
    method: null,
    data: null,
    error,
    ts: Date.now(),
    pos: null,
  };
}

/**
 * Create an event envelope.
 */
export function createEventEnvelope<T>(
  event: string,
  data: T,
  pos: string | null = null,
): Envelope<T> {
  return {
    type: 'event',
    id: generateId(),
    method: event,
    data,
    error: null,
    ts: Date.now(),
    pos,
  };
}

/**
 * Create a system envelope.
 */
export function createSystemEnvelope<T>(subtype: string, data: T): Envelope<T> {
  return {
    type: 'system',
    id: generateId(),
    method: subtype,
    data,
    error: null,
    ts: Date.now(),
    pos: null,
  };
}

// ═══════════════════════════════════════════════════════════════
// TYPE GUARDS
// ═══════════════════════════════════════════════════════════════

/**
 * Check if value is a valid envelope.
 */
export function isEnvelope(value: unknown): value is Envelope {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const obj = value as Record<string, unknown>;
  return (
    typeof obj['type'] === 'string' &&
    typeof obj['id'] === 'string' &&
    typeof obj['ts'] === 'number' &&
    'data' in obj
  );
}

/**
 * Check if envelope is a call (request).
 */
export function isCallEnvelope(envelope: Envelope): boolean {
  return envelope.type === 'call' && envelope.method !== null;
}

/**
 * Check if envelope is a response.
 */
export function isResponseEnvelope(envelope: Envelope): boolean {
  return envelope.type === 'response';
}

/**
 * Check if envelope is an error.
 */
export function isErrorEnvelope(envelope: Envelope): boolean {
  return envelope.type === 'error' || envelope.error !== null;
}

/**
 * Check if envelope is an event.
 */
export function isEventEnvelope(envelope: Envelope): boolean {
  return envelope.type === 'event' && envelope.method !== null;
}

/**
 * Check if envelope is a system message.
 */
export function isSystemEnvelope(envelope: Envelope): boolean {
  return envelope.type === 'system';
}

// ═══════════════════════════════════════════════════════════════
// STANDARD ERRORS
// ═══════════════════════════════════════════════════════════════

/**
 * Create a parse error.
 */
export function parseError(details: unknown | null = null): EnvelopeError {
  return {
    code: String(RPCErrorCode.ParseError),
    message: 'Parse error',
    details,
  };
}

/**
 * Create an invalid request error.
 */
export function invalidRequest(message: string | null = null): EnvelopeError {
  return {
    code: String(RPCErrorCode.InvalidRequest),
    message: message ?? 'Invalid request',
    details: null,
  };
}

/**
 * Create a method not found error.
 */
export function methodNotFound(method: string): EnvelopeError {
  return {
    code: String(RPCErrorCode.MethodNotFound),
    message: `Method not found: ${method}`,
    details: null,
  };
}

/**
 * Create an invalid params error.
 */
export function invalidParams(
  message: string | null = null,
  details: unknown | null = null,
): EnvelopeError {
  return {
    code: String(RPCErrorCode.InvalidParams),
    message: message ?? 'Invalid params',
    details,
  };
}

/**
 * Create an internal error.
 */
export function internalError(
  message: string | null = null,
  details: unknown | null = null,
): EnvelopeError {
  return {
    code: String(RPCErrorCode.InternalError),
    message: message ?? 'Internal error',
    details,
  };
}

/**
 * Create a connection timeout error.
 */
export function connectionTimeout(): EnvelopeError {
  return {
    code: String(RPCErrorCode.ConnectionTimeout),
    message: 'Connection timeout',
    details: null,
  };
}

/**
 * Create a request timeout error.
 */
export function requestTimeout(method: string, timeoutMs: number): EnvelopeError {
  return {
    code: String(RPCErrorCode.RequestTimeout),
    message: `Request timeout: ${method} (${timeoutMs}ms)`,
    details: { method, timeoutMs },
  };
}

/**
 * Create an unauthorized error.
 */
export function unauthorizedError(message: string | null = null): EnvelopeError {
  return {
    code: String(RPCErrorCode.Unauthorized),
    message: message ?? 'Unauthorized',
    details: null,
  };
}

/**
 * Create a forbidden error.
 */
export function forbiddenError(message: string | null = null): EnvelopeError {
  return {
    code: String(RPCErrorCode.Forbidden),
    message: message ?? 'Forbidden',
    details: null,
  };
}
